<script setup lang="ts">
/**
 * The lifeline at the bottom of every screen (START.md §9).
 *
 * If anything here is hard — the video, the typing, the recording — there is a named person at
 * Altamont to write to, behind one real tap target. Email is the only contact route: Altamont
 * does not want to be called, so no number appears here or anywhere else a candidate can see.
 * HR writes all of this copy, so nothing is hard-coded, and until the configuration has arrived
 * this renders nothing at all rather than a half-filled footer or an error.
 */

import { computed } from 'vue';

import { useConfigStore } from '@/stores/config';

const config = useConfigStore();

/** The HR contact block, or `null` before `GET /api/config` has answered. */
const settings = computed(() => config.settings);

const contactName = computed(() => settings.value?.hrContactName.trim() ?? '');
const email = computed(() => settings.value?.hrContactEmail.trim() ?? '');
const accommodations = computed(() => settings.value?.accommodationsText.trim() ?? '');

/** A subject line saves the person from having to explain which thing they are writing about. */
const mailHref = computed(() => {
  const address = email.value;
  if (!address.includes('@')) return '';
  return `mailto:${address}?subject=${encodeURIComponent('Help with my Video Application')}`;
});

/** Nothing worth showing? Then show nothing. */
const visible = computed(
  () => settings.value !== null && (mailHref.value !== '' || accommodations.value !== ''),
);

/** "Dana is here to help." — or the plain version when HR has not named anyone. */
const intro = computed(() =>
  contactName.value === ''
    ? 'Someone at Altamont is here to help.'
    : `${contactName.value} at Altamont is here to help.`,
);
</script>

<template>
  <footer v-if="visible" class="alt-help" aria-labelledby="alt-help-heading">
    <div class="alt-help__inner">
      <h2 id="alt-help-heading" class="alt-help__heading">Need help?</h2>
      <p class="alt-help__intro">{{ intro }}</p>
      <p v-if="accommodations" class="alt-help__note">{{ accommodations }}</p>

      <div v-if="mailHref" class="alt-help__links">
        <a class="alt-btn alt-btn--ghost alt-btn--block" :href="mailHref">Email {{ email }}</a>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.alt-help {
  margin-top: var(--alt-space-8);
  border-top: 1px solid var(--alt-line);
  background-color: var(--alt-cream);
}

.alt-help__inner {
  display: flex;
  flex-direction: column;
  gap: var(--alt-space-3);
  width: 100%;
  max-width: var(--alt-maxw-phone);
  margin-inline: auto;
  padding: var(--alt-space-6) var(--alt-space-5);
  padding-bottom: calc(var(--alt-space-6) + env(safe-area-inset-bottom, 0px));
}

@media (min-width: 720px) {
  .alt-help__inner {
    max-width: var(--alt-maxw-content);
    padding-inline: var(--alt-space-7);
  }
}

.alt-help__heading {
  font-size: var(--alt-text-xl);
}

.alt-help__intro,
.alt-help__note {
  color: var(--alt-navy);
}

.alt-help__note {
  font-size: var(--alt-text-sm);
  color: var(--alt-slate);
}

.alt-help__links {
  display: grid;
  gap: var(--alt-space-3);
  margin-top: var(--alt-space-1);
}

/* A long email address must wrap inside the button rather than push the page sideways. */
.alt-help__links .alt-btn {
  overflow-wrap: anywhere;
  text-align: center;
}

@media (min-width: 480px) {
  .alt-help__links {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}
</style>
